import React, { useState, useEffect, useCallback } from 'react';
import { AiOutlineSearch } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';
import { request } from '../../../api/request.tsx';
import Pagination from '../../../component/Pagination/index.tsx';

interface Translation {
  _id: string;
  language: string;
  title: string;
  content: string;
}

interface NewsItem {
  id: string;
  priority: number;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  bannerUrl: string;
  thumbnailUrl: string;
  videoUrl: string;
  type: string;
  translations: Translation[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  slug: string;
}



const News = () => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [newsData, setNewsData] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedLanguage, setSelectedLanguage] = useState('vi');


  const getTitleByLanguage = (translations: Translation[], language: string) => {
    const translation = translations.find(t => t.language === language);
    return translation ? translation.title : translations[0]?.title || '';
  };

  const fetchData = useCallback(async (page = 1, search = '') => {
    setLoading(true);
    try {
      const params: any = {
        page,
        limit: 10
      };

      if (search) {
        params.title_like = search;
      }

      let statusFilter: string | null = null;
      if (showActive && !showInactive) statusFilter = 'active';
      if (!showActive && showInactive) statusFilter = 'inactive';
      
      if (statusFilter) {
        params.isActive = statusFilter === 'active';
      }

      const response = await request('news', null, 'GET', params);
      
      if (response?.data) {
        setNewsData(response.data.results || []);
        setTotalPages(response.data.totalPages || 1);
      } else {
        setNewsData([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      setNewsData([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [showActive, showInactive]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchData(1, searchValue);
  };

  const handlePageClick = (event: { selected: number }) => {
    const newPage = event.selected + 1;
    setCurrentPage(newPage);
    fetchData(newPage, searchValue);
  };

  const handleUpdateNews = (newsId: string) => {
    // Find the news item to pass its data
    const newsItem = newsData.find(item => item.id === newsId);
    navigate(`/admin/create-news`, { 
      state: { 
        mode: 'update', 
        newsId: newsId,
        initialData: newsItem 
      } 
    });
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [fetchData, currentPage]);

  const filteredNews = newsData;

  return (
    <div className="w-full">
      <div className=""></div>
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-[pink] mb-2">DANH SÁCH THÔNG BÁO</h1>
        </div>

        {/* Search Bar */}
        <div className="flex justify-center w-full my-6">
          <div
            className="
          flex 
          items-center 
          border-[1.5px] 
          md:w-[50%]
          w-full
          rounded-lg
          pl-2
          border-[pink]
          mb-3
          "
          >
            <input
              className="flex w-full px-2 py-2 outline-none "
              placeholder="Tìm kiếm theo tiêu đề"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button
              onClick={handleSearch}
              className="bg-[pink] px-3 h-full rounded-tr-lg rounded-br-lg text-white hover:opacity-80 transition-opacity"
            >
              <AiOutlineSearch size={26} />
            </button>
          </div>
        </div>

        {/* Filter Section */}
        <div className="flex flex-wrap gap-6 mb-6 items-center">
          {/* Status Filters */}
          <div className="flex gap-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showActive}
                onChange={(e) => setShowActive(e.target.checked)}
                className="w-4 h-4 text-[pink] border-[pink] rounded focus:ring-pink-200"
              />
              <span className="text-[pink] font-semibold ml-2">Kích hoạt</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="w-4 h-4 text-gray-500 border-gray-300 rounded focus:ring-gray-200"
              />
              <span className="text-gray-600 font-semibold ml-2">Không kích hoạt</span>
            </label>
          </div>

          {/* Language Filter */}
          <div className="flex items-center gap-2">
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[pink] focus:ring-1 focus:ring-pink-200 bg-white"
            >
              <option value="vi">Tiếng Việt</option>
              <option value="en">English</option>
              {/* <option value="ko">한국어</option> */}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">Ảnh thumb</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">Ảnh banner</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">Tiêu đề</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 border-b border-gray-200">Hành động</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pink-300"></div>
                      <span className="ml-2">Đang tải...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredNews.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                    Không có dữ liệu
                  </td>
                </tr>
              ) : (
                filteredNews.map((item, index) => (
                  <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  {/* Thumbnail */}
                  <td className="px-6 py-4">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                      {item.thumbnailUrl ? (
                        <img 
                          src={item.thumbnailUrl} 
                          alt="Thumbnail" 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.currentTarget as HTMLImageElement;
                            const nextSibling = target.nextElementSibling as HTMLElement;
                            target.style.display = 'none';
                            nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : (
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      )}
                      <div className="hidden w-full h-full items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </div>
                  </td>

                  {/* Banner */}
                  <td className="px-6 py-4">
                    <div className="w-20 h-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                      {item.bannerUrl ? (
                        <img 
                          src={item.bannerUrl} 
                          alt="Banner" 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.currentTarget as HTMLImageElement;
                            const nextSibling = target.nextElementSibling as HTMLElement;
                            target.style.display = 'none';
                            nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : (
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      )}
                      <div className="hidden w-full h-full items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </div>
                  </td>

                  {/* Title */}
                  <td className="px-6 py-4 text-sm text-gray-700">
                    <div className="font-medium">
                      {getTitleByLanguage(item.translations, selectedLanguage)}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4 text-sm">
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleUpdateNews(item.id)}
                        className="bg-blue-500 text-white px-3 py-1 rounded text-xs hover:bg-blue-600 transition-colors"
                      >
                        Cập nhập
                      </button>
                    </div>
                  </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            pageCount={totalPages}
            onPageChange={handlePageClick}
            currentPage={currentPage}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
          />
        )}
    </div>
  );
};

export default News;